import api from 'src/api';
import settings from 'src/features/settingsByApollo';
import { CLEAR_INPUT_ATTACHMENTS_ALL } from 'src/store/modules/activities';
import { SET_USER } from 'src/store/modules/user';
import {
  notice, commandType, channelType, isEmpty, auth, consts, l, crmMessages,
} from 'src/utils';
import { channels as commonChannels } from 'src/utils/notice/common';
import { eventBus } from 'src/features';
import parseJwt from 'src/filters/parseJwt';
import i18n from 'src/features/i18n';
import toast from 'src/utils/toast';
import { prefix } from 'src/config';
import inputMessages from '../../utils/inputMessages';

// в константы выносим только названия мутаций/геттеров,
// которые переиспользуем/планируем переиспользовать
export const focused = 'focused';
export const LOADING_UCCE_INTEGRATION = 'LOADING_UCCE_INTEGRATION';
export const STOP_LOADING_UCCE_INTEGRATION = 'STOP_LOADING_UCCE_INTEGRATION';

function AudioPad(src) {
  let audio;
  if (typeof Audio !== 'undefined') {
    try {
      audio = new Audio(src);
    } catch (err) {
      audio = {
        play() {},
      };
    }
  } else {
    return {
      play() {},
    };
  }
  return audio;
}

const stateData = {
  drawer: false,
  user: {
    id: null,
    name: '',
    email: '',
    role: '',
    absenceReason: '',
    ready: undefined,
    status: undefined,
    overseerTeams: [],
    avatarUrl: null,
    external: null,
  },
  notice: {},
  appearanceReasonsList: [],
  focused: true,
  pushNoticeInternet: null,
  operatorConnected: null,
  status: {},
  oldTitle: '',
  activeTitle: '',
  soundNotification: new AudioPad(`${prefix}/music/notification_sound.mp3`),
  titleIntervalId: null,
  currentTeam: undefined,
  previousTeam: undefined,
  ucceIntegration: false,
  fetchingUcceIntegration: false,
};

const onSuccessSetStatus = (commit, data) => {
  if (data.type === commandType.CHANGE_STATUS) {
    commit('SET_STATUS', data);
    commit('SET_ABSENCE_REASON', data);
  }
};

const onSuccessForceLogout = (data) => {
  if (data.type === commandType.FORCE_LOGOUT) {
    eventBus.$emit(consts.FORCE_LOGOUT);
    eventBus.$emit(consts.ALERT, data.data.message);
  }
};

const onSuccessSimultaneousLogin = (data) => {
  if (data.type === commandType.SIMULTANEOUS_LOGIN) {
    eventBus.$emit(consts.ALERT, data.data.message);
  }
};

const onSuccessJoinSetStatus = async (data, dispatch, rootState) => {
  if (data.type === commandType.JOIN && data.data.topic.replace(/:+\S+/, '') === channelType.WATCHTOWER
    && stateData.user.ready) {
    await dispatch('loadUser', rootState.user.id);
    dispatch('setStatus', {
      message: stateData.user.absenceReason,
      value: stateData.user.ready,
    });
  }
};

const setNotice = (commit, dispatch, data, rootState) => {
  onSuccessSetStatus(commit, data);
  onSuccessForceLogout(data);
  onSuccessSimultaneousLogin(data);
  onSuccessJoinSetStatus(data, dispatch, rootState);
  commit('SET_NOTICE', data);
  if (data.type === commandType.INFO) {
    dispatch('startAutoChangeTitle', data.data.infoMessageText || 'CTI OMNI CENTER');
  }
  if (data.type === commandType.SOCKET_ERROR) {
    commit('SET_PUSH_NOTICE_INTERNET', true);
    commit('SET_CONNECTED', false);
  } else {
    commit('SET_PUSH_NOTICE_INTERNET', false);
    commit('SET_CONNECTED', true);
  }
};

function userReady(userId, result, message) {
  notice.push(userId, commandType.SET_STATUS, { ready: result, message });
}

function subscribe(data) {
  return notice.subscribe(data);
}

function unSubscribe({ userId, channelTypeName }) {
  notice.unSubscribe(userId, channelTypeName);
}

function isSubscribe({ userId, channelTypeName }) {
  return notice.isSubscribe(userId, channelTypeName);
}

function disconnect() {
  notice.disconnect();
}

function channels() {
  return new Map(commonChannels);
}

function getSubscriptionOverseerData({ rootState, state }) {
  const token = auth.getCurrentToken();
  const parsedToken = parseJwt(token);
  const teamIsChanged = state.previousTeam && state.previousTeam !== state.currentTeam;
  const id = isEmpty(parsedToken.team_id) ? null : parsedToken.team_id;
  const overseerId = rootState.user.id;
  return {
    token, parsedToken, teamIsChanged, id, overseerId,
  };
}

function unsubscribeCommon({
  state, overseerId,
}) {
  unSubscribe({
    userId: state.previousTeam,
    channelTypeName: channelType.OVERSEER_TEAM,
  });
  unSubscribe({
    userId: overseerId,
    channelTypeName: channelType.OVERSEER,
  });
  disconnect();
}

const actions = {
  async loadUser({ commit }, userId) {
    if (userId) {
      const userData = await api.getUserById({ id: userId });
      commit('LOAD_USER', { userId, ...userData });
      commit(`user/${SET_USER}`, {
        id: userId, username: userData.name, avatarUrl: userData.avatarUrl, ...userData,
      }, { root: true });
    }
  },
  async getUser(_, userId) {
    return api.getUserById({ id: userId });
  },
  async loadAbsenceReasons({ commit, dispatch, state }) {
    const fetchedMessages = await api.getAbsenceReasons();
    const userId = state.user.id;
    const userData = await api.getUserById({ id: userId });
    const absences = fetchedMessages.map((item) => ({ message: item.message, value: false }));
    if (userData.operatorTeam === null) { // TODO временное решения
      try {
        const activities = await api.getCurrentActivities({ id: userId });
        const isInProgress = activities.some((activity) => activity.state === 'in_progress');
        if (isInProgress) {
          dispatch('setStatus', { message: i18n.t('T_READY'), value: true });
        } else {
          dispatch('setStatus', { message: i18n.t('T_NOT_READY'), value: false });
        }
      } catch (error) {
        dispatch('setStatus', { message: i18n.t('T_NOT_READY'), value: false });
      }

      toast.warning({
        msg: i18n.t('T_OPERATOR_WITHOUT_TEAM_CANNOT_BE_READY'),
        duration: 5000,
      });
    } else {
      absences.splice(0, 0, { value: true, message: i18n.t('T_READY') });
    }

    commit('SET_APPEARANCE_REASONS_LIST', absences);
  },
  async subscribeOverseerAndTeam({
    commit, rootState, dispatch, state,
  }) {
    const {
      token, parsedToken, teamIsChanged, id, overseerId,
    } = getSubscriptionOverseerData({ rootState, state });

    if (
      state.previousTeam
      && isSubscribe({ userId: state.previousTeam, channelTypeName: channelType.OVERSEER_TEAM })
      && teamIsChanged
    ) {
      unsubscribeCommon({
        commit, state, overseerId,
      });
    }
    const shouldSubscribe = parsedToken.team_id
      && !isSubscribe({ userId: state.previousTeam, channelTypeName: channelType.OVERSEER_TEAM })
      && !isSubscribe({ userId: id, channelTypeName: channelType.OVERSEER_TEAM })
      && !(!state.currentTeam && state.previousTeam);
    if (shouldSubscribe) {
      await dispatch('subscribeTeam', { id, token });
      await dispatch('subscribeOverseerChannel', { overseerId, token });
      if (!state.currentTeam) {
        commit('SET_CURRENT_TEAM', parsedToken.team_id);
      }
    } else if (!parsedToken.team_id) {
      unsubscribeCommon({
        commit, state, overseerId,
      });
      commit('SET_CURRENT_TEAM', null);
    }
  },

  async subscribeTeam({ commit, rootState, dispatch }, { id, token }) {
    subscribe({
      id,
      channelType: channelType.OVERSEER_TEAM,
      token,
      username: rootState.user.username,
      callback: (data) => setNotice(commit, dispatch, data, rootState),
    });
  },

  async subscribeOverseerChannel({ commit, rootState, dispatch }, { overseerId, token }) {
    if (!isSubscribe({ userId: overseerId, channelTypeName: channelType.OVERSEER })) {
      subscribe({
        id: overseerId,
        channelType: channelType.OVERSEER,
        token,
        username: rootState.user.username,
        callback: (data) => setNotice(commit, dispatch, data, rootState),
      });
    }
  },

  async subscribe({ commit, rootState, dispatch }, { resourceId, channelTypeName } = {}) {
    // используется только оператором для каналов watchtower и room
    // если переданы resourceId и channelTypeName - то подписка на room, иначе - на watchtower
    const id = isEmpty(resourceId) ? rootState.user.id : resourceId;
    const locChannelType = isEmpty(channelTypeName)
      ? channelType.WATCHTOWER
      : channelTypeName;
    if (rootState.user.role && !isSubscribe({
    // проверка на rootState.user.role исключает краевой случай,
    // когда данные по юзеру еще не получены (актуально для подписки на watchtower)
      userId: rootState.user.id,
      channelTypeName: locChannelType,
    })) {
      subscribe({
        id,
        channelType: locChannelType,
        token: auth.getCurrentToken(),
        username: rootState.user.username,
        callback: (data) => {
          setNotice(commit, dispatch, data, rootState);
        },
      });
    }
  },
  async logout({ commit, rootState, state }, { force } = {}) {
    const userRole = rootState.user.role;
    if (!force) { await api.logout(); }
    if (userRole === 'OVERSEER') {
      unsubscribeCommon({
        commit, state, overseerId: rootState.user.id,
      });
    } else if (userRole === 'OPERATOR') {
      unSubscribe({ userId: rootState.user.id, channelTypeName: channelType.WATCHTOWER });
    }
    if (userRole === 'OPERATOR'
      && rootState.app.isFrame && !rootState.home.ucceIntegration) {
      crmMessages.sendLogoutSuccess();
    }
    if (userRole === 'OPERATOR') {
      inputMessages.clear();
      commit(`activities/${CLEAR_INPUT_ATTACHMENTS_ALL}`, {}, { root: true });
    }
    disconnect();
    commit('LOGOUT');
    commit('SET_CURRENT_TEAM', null);
    const curToken = settings.getCurrentToken();
    auth.clearStorage(curToken);
  },
  registerUser({ commit }, role) {
    settings.updateHeaderAuthorization(role);
    return new Promise((resolve, reject) => {
      if (settings.header.authorization) {
        const promise = api.getCurrentUser().then((res) => {
          commit(`user/${SET_USER}`, { username: res.fullName, ...res }, { root: true });
        });
        resolve(promise);
      } else {
        // TODO: clear incorrect jwt
        l.error(`Токен для ${role} отсутствует или неверен`
          + `  (заголовок авторизации ${settings.header.authorization})`);
        reject();
      }
    });
  },
  setStatus({ commit, rootState }, item) {
    userReady(rootState.user.id, item.value, item.message);
    commit('SET_MENU_STATUS', { message: item.message, ready: item.value });
    commit(`user/${SET_USER}`, { ready: item.value, absenceReason: item.message }, { root: true });
  },
  toggleDrawer({ commit, state }) {
    commit('SET_DRAWER', !state.drawer);
  },
  setDrawer({ commit }, value) {
    commit('SET_DRAWER', value);
  },
  setFocused({ commit }, value) {
    commit('SET_FOCUSED', value);
  },
  updateStatus({ commit }, data) {
    if (data) {
      commit('SET_MENU_STATUS', { message: data.absenceReason, ready: data.ready });
      commit(`user/${SET_USER}`, { ready: data.ready, absenceReason: data.absenceReason }, { root: true });
    }
  },
  newMessagePlaySound({ commit }) {
    commit('NEW_MESSAGE_PLAY_SOUND');
  },
  startAutoChangeTitle({ commit }, text) {
    commit('START_AUTO_CHANGE_TITLE', text);
  },
  stopAutoChangeTitle({ commit }) {
    commit('STOP_AUTO_CHANGE_TITLE');
  },
  channels() {
    return channels;
  },
  changeTeamOverseer({ commit }, id) {
    if (id) {
      api.changeTeam(id).then((res) => {
        auth.setRoleToken(channelType.OVERSEER_TEAM, res);
        commit('SET_CURRENT_TEAM', id);
      });
    } else {
      commit('SET_CURRENT_TEAM', null);
    }
  },
  getUcceIntegration({ commit }) {
    commit(LOADING_UCCE_INTEGRATION);
    return api.getUcceIntegration().then((response) => {
      commit('SET_UCCE', response.enable);
      commit(STOP_LOADING_UCCE_INTEGRATION);
    }).catch((err) => {
      l.error(err);
      commit(STOP_LOADING_UCCE_INTEGRATION);
    });
  },
  updateUcceIntegration({ commit }, value) {
    commit(LOADING_UCCE_INTEGRATION);
    return api.updateUcceIntegration(value).then((response) => {
      commit('SET_UCCE', response.enable);
      commit(STOP_LOADING_UCCE_INTEGRATION);
    }).catch((err) => {
      l.error(err);
      commit(STOP_LOADING_UCCE_INTEGRATION);
    });
  },
  setOldDocumentTitleHome({ commit }, title) {
    commit('SET_OLD_DOCUMENT_TITLE', title);
  },
};

const mutations = {
  LOAD_USER(state, {
    userId: id,
    email, name, ready, status, role, absenceReason,
    overseerTeams, operatorTeam, operatorTeamId, avatarUrl,
    external,
  }) {
    state.user.id = id;
    state.user.email = email;
    state.user.name = name;
    state.user.ready = ready;
    state.user.avatarUrl = avatarUrl;
    if (status) state.user.status = status;
    state.user.role = role.toLowerCase();
    state.user.absenceReason = absenceReason;
    state.user.overseerTeams = overseerTeams;
    state.user.operatorTeam = operatorTeam;
    state.user.operatorTeamId = operatorTeamId;
    state.user.external = external;
  },
  LOGOUT(state) {
    state.user = {
      id: 0,
      name: '',
      email: '',
      role: '',
      ready: false,
      status: 'not_ready',
      absenceReason: undefined,
      overseerTeams: null,
      avatarUrl: null,
      external: null,
    };
  },
  SET_CURRENT_TEAM(state, id) {
    state.previousTeam = state.currentTeam || id;
    state.currentTeam = id;
  },
  SET_NOTICE(state, data) {
    state.notice = data;
  },
  SET_DRAWER(state, drawer) {
    state.drawer = drawer;
  },
  SET_STATUS(state, { data }) {
    state.user.ready = data.ready;
    state.user.status = data.status;
  },
  SET_APPEARANCE_REASONS_LIST(state, data) {
    state.appearanceReasonsList = data;
  },
  SET_ABSENCE_REASON(state, { data }) {
    state.user.absenceReason = data.absenceReason;
  },
  SET_FOCUSED(state, value) {
    if (state.focused !== value) {
      state.focused = value;
    }
  },
  SET_PUSH_NOTICE_INTERNET(state, status) {
    state.pushNoticeInternet = status;
  },
  SET_CONNECTED(state, status) {
    state.operatorConnected = status;
  },
  SET_MENU_STATUS(state, data) {
    if (state.status.ready !== 'undefined' && data.ready !== !!state.status.ready && !data.message) {
      state.status = {};
    } else if (data.message) {
      state.status.message = data.message;
    }
    state.status.ready = data.ready;
  },
  START_AUTO_CHANGE_TITLE(state, value) {
    if (state.titleIntervalId) {
      return;
    }

    state.activeTitle = value;
    const { oldTitle, activeTitle } = state;
    state.titleIntervalId = setInterval(() => {
      document.title = document.title === activeTitle ? oldTitle : activeTitle;
    }, 1000);
  },
  STOP_AUTO_CHANGE_TITLE(state) {
    if (state.titleIntervalId) {
      clearInterval(state.titleIntervalId);
      state.titleIntervalId = null;
      document.title = state.oldTitle;
    }
  },
  NEW_MESSAGE_PLAY_SOUND(state) {
    state.soundNotification.play();
  },
  SET_UCCE(state, value) {
    state.ucceIntegration = value;
  },
  LOADING_UCCE_INTEGRATION(state) {
    state.fetchingUcceIntegration = true;
  },
  STOP_LOADING_UCCE_INTEGRATION(state) {
    state.fetchingUcceIntegration = false;
  },
  SET_OLD_DOCUMENT_TITLE(state, title) {
    state.oldTitle = title;
  },

};

const getters = {
  isReady(state) {
    return state.user.ready;
  },
  absenceReason(state) {
    if (state.user.ready) {
      return i18n.t('T_READY');
    }
    if (state.user.absenceReason === '' || state.user.absenceReason == null) {
      return i18n.t('T_NOT_READY');
    }
    return state.user.absenceReason;
  },
  isConnected(state) {
    if (state.pushNoticeInternet !== null) {
      return !state.pushNoticeInternet;
    }
    return false;
  },
  focused(state) {
    return state.focused;
  },
};

const store = {
  state: stateData,
  actions,
  mutations,
  getters,
  namespaced: true,
};

export default store;
